import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useSellerAuth } from '../context/SellerAuthContext';

const SellerLayout = ({ shopName = '', children }) => {
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [sellerUsername, setSellerUsername] = useState('');
  const { seller, logout } = useSellerAuth();

  useEffect(() => {
    setSellerUsername(seller?.seller_username || '');
  }, [seller]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      const res = await logout();

      if (res && res.ok) {
        navigate(res.redirect || '/seller/manage', { replace: true });
        return;
      }

      alert('로그아웃에 실패했습니다.');
    } catch (err) {
      alert('로그아웃에 실패했습니다.');
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const toggleMobileSearch = () => {
    setMobileSearchOpen((prev) => !prev);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const search = String(fd.get('search') || '').trim();

    if (!search) {
      navigate('/seller/search');
      if (mobileSearchOpen) setMobileSearchOpen(false);
      return;
    }

    navigate(`/seller/search?search=${encodeURIComponent(search)}`);
    if (mobileSearchOpen) setMobileSearchOpen(false);
  };

  const navLinkStyle = ({ isActive }) => ({
    ...styles.sidebarLink,
    ...(isActive ? styles.sidebarLinkActive : {}),
  });

  const headerShopLinkStyle = ({ isActive }) => ({
    ...styles.headerShopLink,
    ...(isActive ? styles.headerShopLinkActive : {}),
  });

  return (
    <div style={styles.page}>
      <style>{responsiveCss}</style>

      <header style={styles.header}>
        <div className="seller-header-left" style={styles.headerShopBox}>
          <NavLink to="/seller" style={headerShopLinkStyle}>
            {shopName}
          </NavLink>
        </div>

        <form
          className="seller-desktop-search"
          role="search"
          onSubmit={handleSearchSubmit}
          style={styles.headerSearchForm}
        >
          <input
            type="search"
            name="search"
            style={styles.headerSearchInput}
          />
          <button
            type="submit"
            style={styles.headerSearchButton}
            aria-label="검색"
          >
            <svg viewBox="0 0 16 16" width="18" height="18" fill="white">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </button>
        </form>

        <div style={styles.headerRight}>
          {!!sellerUsername && (
            <span className="seller-user-name" style={styles.headerUserText}>
              {sellerUsername}님
            </span>
          )}

          {!!sellerUsername && (
            <button
              type="button"
              onClick={handleLogout}
              className="seller-logout-btn"
              style={styles.logoutButton}
            >
              로그아웃
            </button>
          )}

          <button
            type="button"
            onClick={toggleMobileSearch}
            className="seller-mobile-search-btn"
            style={styles.mobileIconButton}
            aria-label="모바일 검색"
          >
            <svg viewBox="0 0 16 16" width="18" height="18" fill="white">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </button>

          <button
            type="button"
            onClick={toggleMobileMenu}
            className="seller-mobile-menu-btn"
            style={styles.mobileMenuButton}
            aria-label="모바일 메뉴"
          >
            <span style={styles.mobileMenuLine}></span>
            <span style={styles.mobileMenuLine}></span>
            <span style={styles.mobileMenuLine}></span>
          </button>
        </div>
      </header>

      {mobileSearchOpen && (
        <div className="seller-mobile-search-wrap" style={styles.mobileSearchWrap}>
          <form
            role="search"
            onSubmit={handleSearchSubmit}
            style={styles.mobileSearchForm}
          >
            <input
              type="search"
              placeholder="검색어 입력..."
              name="search"
              style={styles.mobileSearchInput}
            />
            <button type="submit" style={styles.mobileSearchSubmit}>
              검색
            </button>
          </form>
        </div>
      )}

      {mobileMenuOpen && (
        <div style={styles.overlay} onClick={closeMobileMenu}></div>
      )}

      <div style={styles.containerFluid}>
        <div className="seller-row" style={styles.row}>
          <nav
            id="sidebarMenu"
            className={`seller-sidebar ${mobileMenuOpen ? 'show' : ''}`}
            style={styles.sidebar}
          >
            <div className="seller-mobile-top" style={styles.sidebarMobileTop}>
              <div>
                {!!sellerUsername && (
                  <div style={styles.sidebarMobileWelcome}>
                    {sellerUsername}님 <br />
                    환영합니다.
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={closeMobileMenu}
                style={styles.sidebarCloseButton}
              >
                ×
              </button>
            </div>

            <div className="seller-mobile-divider" style={styles.sidebarDivider}></div>

            <div style={styles.sidebarSticky}>
              <ul style={styles.sidebarMenuList}>
                <li style={styles.sidebarGroup}>
                  <div style={styles.sidebarGroupTitle}>상품관리</div>
                  <NavLink to="/seller/category/list" style={navLinkStyle} onClick={closeMobileMenu}>- 카테고리</NavLink>
                  <NavLink to="/seller/product/list" style={navLinkStyle} onClick={closeMobileMenu}>- 상품목록</NavLink>
                  <NavLink to="/seller/product/add" style={navLinkStyle} onClick={closeMobileMenu}>- 상품등록</NavLink>
                  <NavLink to="/seller/stock" style={navLinkStyle} onClick={closeMobileMenu}>- 재고관리</NavLink>
                  <NavLink to="/seller/hero/slides" style={navLinkStyle} onClick={closeMobileMenu}>- 대문관리</NavLink>
                  <NavLink to="/seller/coupon/manage" style={navLinkStyle} onClick={closeMobileMenu}>- 쿠폰관리</NavLink>
                </li>

                <li style={styles.sidebarGroup}>
                  <div style={styles.sidebarGroupTitle}>주문관리</div>
                  <NavLink to="/seller/notices" style={navLinkStyle} onClick={closeMobileMenu}>- 공지사항</NavLink>
                  <NavLink to="/seller/orders" style={navLinkStyle} onClick={closeMobileMenu}>- 주문목록</NavLink>
                  <NavLink to="/seller/order/logs" style={navLinkStyle} onClick={closeMobileMenu}>- 주문로그</NavLink>
                  <NavLink to="/seller/claim/list" style={navLinkStyle} onClick={closeMobileMenu}>- 반품교환</NavLink>
                  <NavLink to="/seller/review" style={navLinkStyle} onClick={closeMobileMenu}>- 리뷰목록</NavLink>
                  <NavLink to="/seller/qna/list" style={navLinkStyle} onClick={closeMobileMenu}>- QnA목록</NavLink>
                </li>

                <li style={styles.sidebarGroup}>
                  <div style={styles.sidebarGroupTitle}>회원관리</div>
                  <NavLink to="/seller/member/list" style={navLinkStyle} onClick={closeMobileMenu}>- 회원목록</NavLink>
                </li>

                <li style={styles.sidebarGroup}>
                  <div style={styles.sidebarGroupTitle}>운영관리</div>
                  <NavLink to="/seller/kiosk/orders" style={navLinkStyle} onClick={closeMobileMenu}>- 주문현황</NavLink>
                  <NavLink to="/kitchen" style={navLinkStyle} onClick={closeMobileMenu}>- 주방오더</NavLink>
                  <NavLink to="/kiosk" style={navLinkStyle} onClick={closeMobileMenu}>- 키오스크</NavLink>
                  <NavLink to="/seller/table/q" style={navLinkStyle} onClick={closeMobileMenu}>- 포스설정</NavLink>
                  <NavLink to="/seller/waiting/qr" style={navLinkStyle} onClick={closeMobileMenu}>- 웨이팅</NavLink>
                  <NavLink to="/seller/delivery/setting" style={navLinkStyle} onClick={closeMobileMenu}>- 배달설정</NavLink>
                </li>

                <li style={styles.sidebarGroup}>
                  <div style={styles.sidebarGroupTitle}>결제관리</div>
                  <NavLink to="/seller/pay/manage" style={navLinkStyle} onClick={closeMobileMenu}>- 결제상태</NavLink>
                  <NavLink to="/seller/settlement" style={navLinkStyle} onClick={closeMobileMenu}>- 입금상태</NavLink>
                </li>
              </ul>
            </div>
          </nav>

          <main className="seller-main" style={styles.main}>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

const responsiveCss = `
@media (max-width: 767px) {
  .seller-desktop-search {
    display: none !important;
  }

  .seller-mobile-search-wrap {
    display: block !important;
  }

  .seller-mobile-search-btn,
  .seller-mobile-menu-btn {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
  }

  .seller-user-name {
    display: none !important;
  }

  .seller-sidebar {
    position: fixed !important;
    left: -320px !important;
    top: 0 !important;
    width: 280px !important;
    min-width: 280px !important;
    height: 100vh !important;
    z-index: 1100 !important;
    transition: left 0.2s ease;
    overflow-y: auto;
    border-right: 1px solid #dee2e6;
    background: #f8f9fa;
    max-height: 100dvh !important;
  }

  .seller-sidebar.show {
    left: 0 !important;
  }

  .seller-mobile-top,
  .seller-mobile-divider {
    display: block !important;
  }

  .seller-row {
    display: block !important;
  }

  .seller-main {
    width: 100% !important;
    padding: 16px !important;
  }

  .seller-header-left {
    width: auto !important;
    min-width: 0 !important;
    flex: 1 !important;
  }
}

@media (min-width: 768px) {
  .seller-mobile-search-wrap {
    display: none !important;
  }

  .seller-mobile-search-btn,
  .seller-mobile-menu-btn {
    display: none !important;
  }

  .seller-mobile-top,
  .seller-mobile-divider {
    display: none !important;
  }
}
`;

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    fontFamily: "'Noto Sans KR', sans-serif",
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    background: '#212529',
    color: '#fff',
    minHeight: '48px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    flexWrap: 'wrap',
  },

  headerShopBox: {
    width: '200px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    background: '#212529',
    padding: 0,
    flexShrink: 0,
  },

  headerShopLink: {
    color: '#fff',
    textDecoration: 'none',
    width: '100%',
    display: 'block',
    paddingLeft: '16px',
    paddingRight: '16px',
    fontSize: '14px',
    lineHeight: '48px',
  },

  headerShopLinkActive: {
    color: '#fff',
  },

  headerSearchForm: {
    display: 'flex',
    flexGrow: 1,
    height: '48px',
    margin: 0,
  },

  headerSearchInput: {
    width: '100%',
    paddingLeft: '20px',
    border: 'none',
    outline: 'none',
    borderRadius: 0,
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#fff',
    fontSize: '14px',
  },

  headerSearchButton: {
    background: 'transparent',
    border: 'none',
    padding: '0 12px',
    cursor: 'pointer',
    flexShrink: 0,
  },

  headerRight: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    gap: '8px',
    minHeight: '48px',
    flexShrink: 0,
  },

  headerUserText: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.5)',
  },

  logoutButton: {
    fontSize: '12px',
    color: '#fff',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },

  mobileIconButton: {
    display: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#fff',
  },

  mobileMenuButton: {
    display: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '3px',
  },

  mobileMenuLine: {
    display: 'block',
    width: '18px',
    height: '2px',
    background: '#fff',
  },

  mobileSearchWrap: {
    display: 'none',
  },

  mobileSearchForm: {
    background: '#212529',
    padding: '12px',
    display: 'flex',
    margin: 0,
  },

  mobileSearchInput: {
    flex: 1,
    border: '1px solid rgba(255,255,255,0.2)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#fff',
    outline: 'none',
    padding: '10px 12px',
    fontSize: '14px',
  },

  mobileSearchSubmit: {
    marginLeft: '8px',
    border: 'none',
    background: '#198754',
    color: '#fff',
    padding: '10px 14px',
    cursor: 'pointer',
    flexShrink: 0,
  },

  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    zIndex: 1090,
  },

  containerFluid: {
    width: '100%',
  },

  row: {
    display: 'flex',
    alignItems: 'stretch',
  },

  sidebar: {
    width: '240px',
    minWidth: '240px',
    background: '#f8f9fa',
    borderRight: '1px solid #dee2e6',
    minHeight: 'calc(100vh - 48px)',
  },

  sidebarMobileTop: {
    display: 'none',
    padding: '16px',
    background: '#f8f9fa',
    color: '#212529',
    position: 'relative',
  },

  sidebarMobileWelcome: {
    fontWeight: 700,
    fontSize: '1.25rem',
    color: '#000',
    lineHeight: 1.4,
  },

  sidebarCloseButton: {
    position: 'absolute',
    top: '10px',
    right: '12px',
    border: 'none',
    background: 'transparent',
    fontSize: '28px',
    lineHeight: 1,
    cursor: 'pointer',
    color: '#333',
  },

  sidebarDivider: {
    display: 'none',
    height: '1px',
    background: '#ddd',
    margin: '0 16px',
  },

  sidebarSticky: {
    position: 'sticky',
    top: 0,
    padding: '20px 0',
  },

  sidebarMenuList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },

  sidebarGroup: {
    marginBottom: '20px',
  },

  sidebarGroupTitle: {
    fontWeight: 700,
    padding: '0 16px 10px 16px',
    color: '#212529',
    fontSize: '14px',
  },

  sidebarLink: {
    display: 'block',
    padding: '8px 24px',
    color: '#495057',
    textDecoration: 'none',
    fontSize: '13px',
  },

  sidebarLinkActive: {
    color: '#111',
    fontWeight: 700,
    backgroundColor: '#e9ecef',
  },

  main: {
    flex: 1,
    padding: '24px',
    fontSize: '12px',
    minWidth: 0,
    background: '#ffffff',
  },
};

export default SellerLayout;